import { INITIAL_AUDIO_STATE, MIN_SEEK_VALUE } from '@/components/emotion-detector/AudioPlayer/constants';
import { CurrentAudio } from '@/components/emotion-detector/AudioPlayer/types';

interface AudioSetupParams {
	audioUrl: string;
	title: string;
	thumbnailUrl?: string;
	programTitle?: string;
	currentAudio: CurrentAudio;
	setCurrentAudio: React.Dispatch<React.SetStateAction<CurrentAudio>>;
}

/**
 * Stops the currently playing audio and resets to initial state
 */
export function stopCurrentAudio(
	currentAudio: CurrentAudio,
	setCurrentAudio: React.Dispatch<React.SetStateAction<CurrentAudio>>,
): void {
	if (currentAudio.element) {
		currentAudio.element.pause();
		currentAudio.element.currentTime = MIN_SEEK_VALUE;
	}
	setCurrentAudio(INITIAL_AUDIO_STATE);
}

/**
 * Creates and sets up an audio element with event listeners
 */
export async function setupAndPlayAudio({
	audioUrl,
	title,
	thumbnailUrl,
	programTitle,
	currentAudio,
	setCurrentAudio,
}: AudioSetupParams): Promise<void> {
	// Stop any currently playing audio
	if (currentAudio.element) {
		currentAudio.element.pause();
		currentAudio.element.currentTime = MIN_SEEK_VALUE;
	}

	// Create new audio element
	const audioElement = new Audio(audioUrl);

	// Set up event listeners
	audioElement.addEventListener('loadedmetadata', () => {
		setCurrentAudio(prev => ({
			...prev,
			duration: audioElement.duration,
		}));
	});

	audioElement.addEventListener('timeupdate', () => {
		setCurrentAudio(prev => ({
			...prev,
			currentTime: audioElement.currentTime,
		}));
	});

	audioElement.addEventListener('ended', () => {
		setCurrentAudio(prev => ({
			...prev,
			isPlaying: false,
		}));
	});

	// Start playing
	await audioElement.play();

	// Update state
	setCurrentAudio({
		element: audioElement,
		title,
		isPlaying: true,
		currentTime: MIN_SEEK_VALUE,
		duration: audioElement.duration || MIN_SEEK_VALUE,
		thumbnailUrl,
		programTitle,
	});
}
